'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';

const journeyNodes = [
  {
    id: 1,
    label: 'GUIDED INTRO',
    sublabel: 'Entry Point',
    href: '/guided-intro',
    moduleId: 'guided-intro',
    angle: -90,
    desc: 'Begin here — orientation to the Haal Project framework',
    type: 'page',
    color: '#D4A82A',
  },
  {
    id: 2,
    label: 'GUIDED WALKTHROUGH',
    sublabel: 'Structured Path',
    href: '/guided-walkthrough',
    moduleId: 'guided-walkthrough',
    angle: -50,
    desc: 'Step-by-step journey through the recognition framework',
    type: 'page',
    color: '#c9a84c',
  },
  {
    id: 3,
    label: 'COMMUNITY',
    sublabel: 'Caretakers Assembling',
    href: '/community',
    moduleId: 'community',
    angle: -10,
    desc: 'Garment patterns, resonance pathways, caretaker stories',
    type: 'page',
    color: '#2d7a47',
  },
  {
    id: 4,
    label: 'KEYS TO KULLI HAAL',
    sublabel: 'Curriculum',
    href: '/keys-to-kulli-haal',
    moduleId: 'keys',
    angle: 30,
    desc: 'Ayat · Haal · Taqwah · Mizan · Amanah',
    type: 'page',
    color: '#D4A82A',
  },
  {
    id: 5,
    label: 'AYAT IN CREATION',
    sublabel: 'Open-World Journey',
    href: '/ayat-in-creation',
    moduleId: 'ayat',
    angle: 70,
    desc: 'Signs pointing back to Allah in the horizons and in yourselves',
    type: 'page',
    color: '#2d7a47',
  },
  {
    id: 6,
    label: 'INNER REALITY LENS',
    sublabel: 'Application Tool',
    href: '/inner-reality-lens',
    moduleId: 'irl',
    angle: 110,
    desc: 'Names of Allah as lens filters — Mata to Uboor',
    type: 'page',
    color: '#2d8a87',
  },
  {
    id: 7,
    label: 'IRL LENS WALKTHROUGH',
    sublabel: 'Guided Application',
    href: '/inner-reality-lens',
    moduleId: 'irl-walkthrough',
    angle: 150,
    desc: 'Structured walkthrough of the Inner Reality Lens layers',
    type: 'page',
    color: '#2d8a87',
  },
  {
    id: 8,
    label: 'MUSLIM RESOURCE GUIDE',
    sublabel: 'Community Utility',
    href: '/muslim-resource-guide',
    moduleId: 'mrg',
    angle: 190,
    desc: 'City resources · Justice-involved · Caretaker networks',
    type: 'page',
    color: '#8a8ab8',
  },
  {
    id: 9,
    label: 'NAMES REFERENCE',
    sublabel: 'Ontological Vocabulary',
    href: '/names-reference',
    moduleId: 'names',
    angle: 230,
    desc: '99 Names of Allah — the vocabulary of recognition',
    type: 'page',
    color: '#8a8ab8',
  },
];

export default function JourneyMapSection() {
  const [hovered, setHovered] = useState<number | null>(null);
  const [visitedModules, setVisitedModules] = useState<Set<string>>(new Set());
  const [currentPosition, setCurrentPosition] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const R = 200;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const visited = new Set<string>();

    const moduleKeys: Record<string, string> = {
      'guided-intro': 'haal_visited_guided_intro',
      'guided-walkthrough': 'haal_visited_guided_walkthrough',
      community: 'haal_visited_community',
      keys: 'haal_visited_keys',
      ayat: 'haal_visited_ayat',
      irl: 'haal_visited_irl',
      'irl-walkthrough': 'haal_visited_irl_walkthrough',
      mrg: 'haal_visited_mrg',
      names: 'haal_visited_names',
    };

    Object.entries(moduleKeys).forEach(([moduleId, key]) => {
      if (localStorage.getItem(key)) visited.add(moduleId);
    });

    const recentModule = sessionStorage.getItem('haal_last_module');
    if (recentModule) visited.add(recentModule);

    setVisitedModules(visited);

    let lastVisited: number | null = null;
    for (let i = journeyNodes.length - 1; i >= 0; i--) {
      if (visited.has(journeyNodes[i].moduleId)) {
        lastVisited = journeyNodes[i].id;
        break;
      }
    }
    setCurrentPosition(lastVisited ?? 1);
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (cursorRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
      }
    };
    section.addEventListener('mousemove', handleMouseMove);
    return () => section.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const isVisited = (node: typeof journeyNodes[0]) => visitedModules.has(node.moduleId);
  const isCurrent = (node: typeof journeyNodes[0]) => node.id === currentPosition;

  return (
    <section ref={sectionRef} className="relative py-32 interactive-section overflow-hidden" id="journey">
      {/* Cursor glow */}
      <div
        ref={cursorRef}
        style={{
          position: 'fixed',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,105,20,0.1) 0%, transparent 65%)',
          pointerEvents: 'none',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          willChange: 'transform',
        }}
      />
      <div className="pixel-border-gold-top" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(139,105,20,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(139,105,20,0.05) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          zIndex: 0,
        }}
      />
      <div className="absolute pointer-events-none" style={{ top: '10%', right: '-5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,105,20,0.12) 0%, transparent 70%)', filter: 'blur(80px)', zIndex: 0 }} />
      <div className="absolute pointer-events-none" style={{ bottom: '5%', left: '-5%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(44,74,62,0.4) 0%, transparent 70%)', filter: 'blur(60px)', zIndex: 0 }} />

      <div className="max-w-[1280px] mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.7)' }}>The Haal Project</p>
          <h2 className="font-mono text-4xl md:text-5xl font-black tracking-tight uppercase" style={{ color: '#F5F0E8' }}>Journey Map</h2>
          <p className="font-mono text-sm mt-4 max-w-xl mx-auto" style={{ color: 'rgba(245,240,232,0.4)' }}>
            Nine pages radiating from the Open Letter — each a key, each a passage
          </p>
          {visitedModules.size > 0 && (
            <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-full" style={{ background: 'rgba(139,105,20,0.08)', border: '1px solid rgba(139,105,20,0.2)' }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#B38A2A' }} />
              <span className="font-mono text-[10px] tracking-widest uppercase" style={{ color: 'rgba(179,138,42,0.8)' }}>
                {visitedModules.size} of {journeyNodes.length} stations visited
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-16">

          {/* SVG Radial Map */}
          <div className="relative flex-shrink-0" style={{ width: 520, height: 520 }}>
            <svg viewBox="-260 -260 520 520" width="520" height="520" className="overflow-visible">
              {/* Concentric rings */}
              {[60, 110, 160, 210].map((r) => (
                <circle key={r} cx="0" cy="0" r={r} fill="none" stroke="rgba(139,105,20,0.12)" strokeWidth="1" />
              ))}

              {/* Octagram star lines */}
              {[0, 45, 90, 135].map((angle) => (
                <line
                  key={angle}
                  x1={-220 * Math.cos((angle * Math.PI) / 180)}
                  y1={-220 * Math.sin((angle * Math.PI) / 180)}
                  x2={220 * Math.cos((angle * Math.PI) / 180)}
                  y2={220 * Math.sin((angle * Math.PI) / 180)}
                  stroke="rgba(139,105,20,0.06)"
                  strokeWidth="1"
                />
              ))}

              {/* Journey path arc — connects sequential nodes */}
              {journeyNodes.map((node, idx) => {
                if (idx === 0) return null;
                const prev = journeyNodes[idx - 1];
                const radCurr = (node.angle * Math.PI) / 180;
                const radPrev = (prev.angle * Math.PI) / 180;
                const x1 = R * Math.cos(radPrev);
                const y1 = R * Math.sin(radPrev);
                const x2 = R * Math.cos(radCurr);
                const y2 = R * Math.sin(radCurr);
                const bothVisited = isVisited(node) && isVisited(prev);
                return (
                  <line
                    key={`path-${idx}`}
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={bothVisited ? 'rgba(179,138,42,0.35)' : 'transparent'}
                    strokeWidth="1"
                    strokeDasharray="3 3"
                    style={{ transition: 'stroke 0.4s ease' }}
                  />
                );
              })}

              {/* Radial lines to nodes */}
              {journeyNodes.map((node) => {
                const rad = (node.angle * Math.PI) / 180;
                const x = R * Math.cos(rad);
                const y = R * Math.sin(rad);
                const active = hovered === node.id;
                const visited = isVisited(node);
                return (
                  <line
                    key={node.id}
                    x1="0" y1="0"
                    x2={x} y2={y}
                    stroke={active ? 'rgba(179,138,42,0.7)' : visited ? 'rgba(139,105,20,0.35)' : 'rgba(139,105,20,0.12)'}
                    strokeWidth={active ? '1.5' : visited ? '1' : '0.5'}
                    style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}
                  />
                );
              })}

              {/* Center disc — Open Letter */}
              <circle cx="0" cy="0" r="52" fill="#1C2B25" stroke="rgba(179,138,42,0.5)" strokeWidth="1.5" />
              <circle cx="0" cy="0" r="44" fill="#1C2B25" stroke="rgba(139,105,20,0.2)" strokeWidth="0.5" />
              <text x="0" y="-10" textAnchor="middle" fill="#F5F0E8" fontSize="7" fontFamily="monospace" fontWeight="900" letterSpacing="1" style={{ pointerEvents: 'none' }}>OPEN</text>
              <text x="0" y="2" textAnchor="middle" fill="#F5F0E8" fontSize="7" fontFamily="monospace" fontWeight="900" letterSpacing="1" style={{ pointerEvents: 'none' }}>LETTER</text>
              <text x="0" y="16" textAnchor="middle" fill="rgba(179,138,42,0.5)" fontSize="5" fontFamily="monospace" letterSpacing="2" style={{ pointerEvents: 'none' }}>DEEP GREEN</text>
              {/* Center is clickable */}
              <Link href="/open-letter">
                <circle cx="0" cy="0" r="52" fill="transparent" stroke="none" style={{ cursor: 'pointer' }} />
              </Link>

              {/* Journey nodes */}
              {journeyNodes.map((node) => {
                const rad = (node.angle * Math.PI) / 180;
                const x = R * Math.cos(rad);
                const y = R * Math.sin(rad);
                const isHovered = hovered === node.id;
                const visited = isVisited(node);
                const current = isCurrent(node);
                const nodeR = 16;

                return (
                  <g key={node.id} transform={`translate(${x}, ${y})`}>
                    {/* Hit area — first, base layer, captures all mouse events */}
                    <circle
                      cx="0" cy="0"
                      r={nodeR + 10}
                      fill="transparent"
                      stroke="none"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHovered(node.id)}
                      onMouseLeave={() => setHovered(null)}
                    />
                    {/* Current position pulse ring */}
                    {current && (
                      <circle
                        cx="0" cy="0"
                        r={nodeR + 6}
                        fill="none"
                        stroke="rgba(179,138,42,0.4)"
                        strokeWidth="1"
                        strokeDasharray="3 3"
                        style={{ pointerEvents: 'none' }}
                      />
                    )}
                    {/* Hover glow ring — always rendered, opacity controlled */}
                    <circle
                      cx="0" cy="0"
                      r={nodeR + 4}
                      fill="none"
                      stroke={node.color}
                      strokeWidth="1"
                      opacity={isHovered ? 0.35 : 0}
                      style={{ transition: 'opacity 0.2s ease', pointerEvents: 'none' }}
                    />
                    {/* Main node circle */}
                    <circle
                      cx="0" cy="0"
                      r={nodeR}
                      fill={isHovered ? node.color : visited ? `${node.color}25` : '#1C2B25'}
                      stroke={isHovered ? node.color : visited ? `${node.color}80` : `${node.color}50`}
                      strokeWidth={visited ? '1.5' : '1'}
                      style={{ transition: 'all 0.25s ease', pointerEvents: 'none' }}
                    />
                    {/* Visited dot */}
                    {visited && !isHovered && (
                      <circle cx="0" cy="0" r="3" fill="rgba(179,138,42,0.7)" style={{ pointerEvents: 'none' }} />
                    )}
                    {/* Node number */}
                    <text
                      x="0" y="4"
                      textAnchor="middle"
                      fill={isHovered ? '#1C2B25' : visited ? node.color : '#F5F0E8'}
                      fontSize="8"
                      fontFamily="monospace"
                      fontWeight="900"
                      style={{ transition: 'all 0.25s ease', pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {node.id}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Node List */}
          <div className="flex-1 flex flex-col gap-3">
            {/* Legend */}
            <div className="flex items-center gap-4 mb-2">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: 'rgba(179,138,42,0.7)' }} />
                <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(245,240,232,0.3)' }}>Visited</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: 'rgba(245,240,232,0.15)' }} />
                <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(245,240,232,0.3)' }}>Not yet visited</span>
              </div>
              {currentPosition && (
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full border" style={{ borderColor: 'rgba(179,138,42,0.5)' }} />
                  <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(245,240,232,0.3)' }}>Current</span>
                </div>
              )}
            </div>

            {/* Open Letter center node */}
            <Link
              href="/open-letter"
              className="flex items-start gap-4 p-4 border transition-all duration-300 mb-1"
              style={{
                borderColor: 'rgba(179,138,42,0.4)',
                background: 'rgba(139,105,20,0.06)',
              }}
            >
              <span
                className="font-mono text-xs font-black flex-shrink-0 w-6 h-6 flex items-center justify-center border"
                style={{ borderColor: 'rgba(179,138,42,0.6)', color: '#D4A82A', background: 'rgba(139,105,20,0.15)' }}
              >
                ◈
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs font-black tracking-widest uppercase" style={{ color: '#D4A82A' }}>
                    OPEN LETTER
                  </span>
                  <span className="font-mono text-[8px] tracking-widest uppercase px-1.5 py-0.5" style={{ background: 'rgba(179,138,42,0.12)', border: '1px solid rgba(179,138,42,0.3)', color: 'rgba(179,138,42,0.8)' }}>
                    center
                  </span>
                </div>
                <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.3)' }}>Deep Green — the foundation document at the heart of the journey</p>
              </div>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="flex-shrink-0 mt-1">
                <path d="M2 6h8M6 2l4 4-4 4" stroke="rgba(179,138,42,0.5)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>

            {journeyNodes.map((node) => {
              const visited = isVisited(node);
              const current = isCurrent(node);
              return (
                <Link
                  key={node.id}
                  href={node.href}
                  onMouseEnter={() => setHovered(node.id)}
                  onMouseLeave={() => setHovered(null)}
                  className="group flex items-start gap-4 p-4 border transition-all duration-300"
                  style={{
                    borderColor: hovered === node.id
                      ? `${node.color}60`
                      : current
                      ? 'rgba(179,138,42,0.3)'
                      : visited
                      ? `${node.color}30`
                      : 'rgba(139,105,20,0.15)',
                    background: hovered === node.id
                      ? `${node.color}08`
                      : current
                      ? 'rgba(139,105,20,0.05)'
                      : 'rgba(28,43,37,0.4)',
                  }}
                >
                  <span
                    className="font-mono text-xs font-black flex-shrink-0 w-6 h-6 flex items-center justify-center border transition-all duration-300"
                    style={{
                      borderColor: visited ? `${node.color}60` : `${node.color}30`,
                      color: visited ? node.color : '#F5F0E8',
                      background: hovered === node.id ? `${node.color}20` : visited ? `${node.color}10` : 'transparent',
                    }}
                  >
                    {visited ? '✓' : node.id}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="font-mono text-xs font-black tracking-widest uppercase transition-colors duration-200"
                        style={{ color: hovered === node.id ? node.color : visited ? `${node.color}CC` : '#F5F0E8' }}
                      >
                        {node.label}
                      </span>
                      {current && (
                        <span className="font-mono text-[8px] tracking-widest uppercase px-1.5 py-0.5" style={{ background: 'rgba(179,138,42,0.12)', border: '1px solid rgba(179,138,42,0.3)', color: 'rgba(179,138,42,0.8)' }}>
                          current
                        </span>
                      )}
                    </div>
                    <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.3)' }}>{node.desc}</p>
                  </div>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={`flex-shrink-0 mt-1 transition-transform duration-300 ${hovered === node.id ? 'translate-x-1' : ''}`}>
                    <path d="M2 6h8M6 2l4 4-4 4" stroke={hovered === node.id ? `${node.color}90` : 'rgba(139,105,20,0.3)'} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
      <div className="pixel-border-gold-bottom" />
    </section>
  );
}
